/**
 * Server functions لمنشئ الصفحات للأدمن (`/admin/pages` + `/p/$slug`).
 *
 * جدولان:
 *  - custom_pages: الصفحة نفسها (slug + عناوين بلغتين + SEO + نشر).
 *  - custom_page_blocks: بلوكات داخل الصفحة (sort_order + content jsonb ar/en).
 *
 * قراءة عامّة للصفحات المنشورة فقط (RLS). كتابة admin فقط.
 *
 * ملاحظة: نستخدم `(supabaseAdmin.from(...) as any)` للجدولين الجديدين لأنّ
 * types.ts التلقائي لـSupabase لا يتضمّنهما إلّا بعد المزامنة التالية.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";

export type BlockType =
  | "hero"
  | "text"
  | "image"
  | "banner"
  | "cta"
  | "products"
  | "video"
  | "steps"
  | "gap";

export const BLOCK_TYPES: readonly BlockType[] = [
  "hero",
  "text",
  "image",
  "banner",
  "cta",
  "products",
  "video",
  "steps",
  "gap",
] as const;

// content jsonb: شكله يعتمد على block_type — نتركه فضفاضاً ونصدّق على بنيته في كلّ بلوك.
export type BlockContent = Record<string, unknown>;

export interface CustomPageRow {
  id: string;
  slug: string;
  title_ar: string;
  title_en: string;
  seo_description_ar: string;
  seo_description_en: string;
  og_image_url: string | null;
  is_published: boolean;
  show_breadcrumbs: boolean;
  show_in_sitemap: boolean;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomPageBlockRow {
  id: string;
  page_id: string;
  block_type: BlockType;
  sort_order: number;
  is_visible: boolean;
  // jsonb — قابل للتسلسل دائماً (نستعمل any للتوافق مع TanStack serializer)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
  created_at: string;
  updated_at: string;
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const adminTable = (name: string): any => (supabaseAdmin.from as any)(name);

// ============== Public (لا حماية auth — قراءة عامّة للمنشور فقط) ==============

export const getPublicPageBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1).max(64) }).parse(d))
  .handler(async ({ data }) => {
    const { data: page, error } = await adminTable("custom_pages")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!page) return { page: null, blocks: [] as CustomPageBlockRow[] };

    const { data: blocks, error: e2 } = await adminTable("custom_page_blocks")
      .select("*")
      .eq("page_id", page.id)
      .eq("is_visible", true)
      .order("sort_order", { ascending: true });
    if (e2) throw new Error(e2.message);

    return {
      page: page as CustomPageRow,
      blocks: (blocks ?? []) as CustomPageBlockRow[],
    };
  });

export const listPublicPages = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await adminTable("custom_pages")
    .select("slug, title_ar, title_en, updated_at, show_in_sitemap")
    .eq("is_published", true)
    .order("updated_at", { ascending: false });
  if (error) throw new Error(error.message);
  return {
    pages: (data ?? []) as Array<{
      slug: string;
      title_ar: string;
      title_en: string;
      updated_at: string;
      show_in_sitemap: boolean;
    }>,
  };
});

// ============== Admin ==============

export const listAdminPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { data, error } = await adminTable("custom_pages")
      .select("*")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { pages: (data ?? []) as CustomPageRow[] };
  });

export const getAdminPage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { data: page, error } = await adminTable("custom_pages")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!page) throw new Error("الصفحة غير موجودة");
    const { data: blocks, error: e2 } = await adminTable("custom_page_blocks")
      .select("*")
      .eq("page_id", data.id)
      .order("sort_order", { ascending: true });
    if (e2) throw new Error(e2.message);
    return {
      page: page as CustomPageRow,
      blocks: (blocks ?? []) as CustomPageBlockRow[],
    };
  });

export const createCustomPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        slug: z.string().min(1).max(64).regex(SLUG_RE, "slug غير صالح (أحرف صغيرة وأرقام و - فقط)"),
        title_ar: z.string().min(1).max(200),
        title_en: z.string().min(1).max(200),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const exists = await adminTable("custom_pages")
      .select("id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (exists.data) throw new Error("slug مستخدم بالفعل");
    const { data: page, error } = await adminTable("custom_pages")
      .insert({
        slug: data.slug,
        title_ar: data.title_ar,
        title_en: data.title_en,
        created_by: context.userId,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return { page: page as CustomPageRow };
  });

export const updateCustomPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        slug: z.string().min(1).max(64).regex(SLUG_RE).optional(),
        title_ar: z.string().min(1).max(200).optional(),
        title_en: z.string().min(1).max(200).optional(),
        seo_description_ar: z.string().max(300).optional(),
        seo_description_en: z.string().max(300).optional(),
        og_image_url: z.string().max(2000).nullable().optional(),
        is_published: z.boolean().optional(),
        show_breadcrumbs: z.boolean().optional(),
        show_in_sitemap: z.boolean().optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { id, ...rest } = data;
    const patch: Record<string, unknown> = { ...rest };
    if (rest.is_published === true) {
      const cur = await adminTable("custom_pages")
        .select("published_at")
        .eq("id", id)
        .maybeSingle();
      if (!cur.data?.published_at) patch.published_at = new Date().toISOString();
    }
    const { data: page, error } = await adminTable("custom_pages")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return { page: page as CustomPageRow };
  });

export const deleteCustomPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { error } = await adminTable("custom_pages").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============== Blocks ==============

export const createBlock = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        page_id: z.string().uuid(),
        block_type: z.enum(BLOCK_TYPES as unknown as [BlockType, ...BlockType[]]),
        content: z.record(z.string(), z.unknown()).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const last = await adminTable("custom_page_blocks")
      .select("sort_order")
      .eq("page_id", data.page_id)
      .order("sort_order", { ascending: false })
      .limit(1)
      .maybeSingle();
    const nextOrder = ((last.data?.sort_order as number | undefined) ?? -1) + 1;
    const { data: block, error } = await adminTable("custom_page_blocks")
      .insert({
        page_id: data.page_id,
        block_type: data.block_type,
        content: data.content ?? {},
        sort_order: nextOrder,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return { block: block as CustomPageBlockRow };
  });

export const updateBlock = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        content: z.record(z.string(), z.unknown()).optional(),
        is_visible: z.boolean().optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { id, ...patch } = data;
    const { data: block, error } = await adminTable("custom_page_blocks")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return { block: block as CustomPageBlockRow };
  });

export const deleteBlock = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { error } = await adminTable("custom_page_blocks").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderBlocks = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        page_id: z.string().uuid(),
        ordered_ids: z.array(z.string().uuid()).min(1).max(200),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const updates = await Promise.all(
      data.ordered_ids.map((id, idx) =>
        adminTable("custom_page_blocks")
          .update({ sort_order: idx })
          .eq("id", id)
          .eq("page_id", data.page_id),
      ),
    );
    const firstErr = updates.find((u: { error: unknown }) => u.error)?.error as
      | { message?: string }
      | undefined;
    if (firstErr) throw new Error(firstErr.message ?? "reorder failed");
    return { ok: true };
  });
